import { translate as t } from "./locale";
import { AiAssistant } from "./ai-assistant";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
    type CollaborationEnvelope,
    decryptProjectPayload,
    encryptProjectPayload,
    parseTrainerFragment,
    randomSecret,
    trainerShareLink,
} from "./collaboration-crypto";
import {
    collaborationChangeMessage,
    type CollaborationOwnerType,
    hasTrainerAccess,
    nextOwnerPushState,
    shouldAcceptPolledRevision,
    trainerSynchronizationStatus,
    trainerPreflightFailure,
} from "./collaboration-state";
import {
    EditorShell,
    PanelHeader,
    StatusGrid,
    TRAINER_PANEL_TITLE,
} from "./editor-shell";
import { downloadProject, settleProject } from "./project-files";
import { ProjectImportDialog } from "./project-import-dialog";
import { loadScratchProject } from "./project-import";
import { createRandomProjectName } from "./project-name";
import { ScratchEditor, type ScratchVm } from "./scratch-editor";
import { broadcastLogout, subscribeToLogout } from "./session-sync";
import {
    authenticatedAccountLabel,
    csrfHeaders,
    readSession,
    type AuthenticatedSession,
    type Session,
} from "./session";
import { StudentLoginForm } from "./student-login";
import { TrainerAccessFrame } from "./trainer-access";
import { setTrainerTabTitle } from "./trainer-tab-title";
import {
    registerTelemetrySensitiveValue,
    trackTelemetryAction,
    trackTelemetryError,
} from "./telemetry";

interface CollaborationHead {
    authorRole: "student" | "trainer" | null;
    envelope: CollaborationEnvelope | null;
    expiresAt: string;
    ownerAccountType: CollaborationOwnerType;
    ownerActive?: boolean;
    revision: number;
}

interface StudentShare {
    id: string;
    key: string;
    ownerCapability?: string;
    revision: number;
    shareUrl?: string;
    trainerCapability: string;
}

const capabilityHeaders = (capability: string): Record<string, string> => ({
    Authorization: `Capability ${capability}`,
});

const studentStorageKey = (projectId: string): string =>
    `scratch-cg-student-share:${projectId}`;
const trainerStorageKey = (sessionId: string): string =>
    `scratch-cg-trainer-share:${sessionId}`;

const readStoredShare = (projectId: string): StudentShare | undefined => {
    const stored = localStorage.getItem(studentStorageKey(projectId));
    if (!stored) return undefined;
    try {
        return JSON.parse(stored) as StudentShare;
    } catch {
        localStorage.removeItem(studentStorageKey(projectId));
        return undefined;
    }
};

export const moveStoredStudentShare = (
    sourceProjectId: string,
    targetProjectId: string,
): void => {
    const stored = localStorage.getItem(studentStorageKey(sourceProjectId));
    if (!stored) return;
    localStorage.setItem(studentStorageKey(targetProjectId), stored);
    localStorage.removeItem(studentStorageKey(sourceProjectId));
};

const readHead = async (
    id: string,
    capability?: string,
    revision?: number,
    includePresence = false,
): Promise<CollaborationHead | undefined> => {
    const response = await fetch(
        `/api/collaboration/v1/sessions/${id}/head${includePresence ? "?presence=true" : ""}`,
        {
            headers: {
                ...(capability ? capabilityHeaders(capability) : {}),
                ...(revision === undefined
                    ? {}
                    : { "If-None-Match": `"${revision}"` }),
            },
        },
    );
    if (response.status === 304) return undefined;
    if (!response.ok) {
        const error = new Error("COLLABORATION_HEAD_FAILED") as Error & {
            status?: number;
        };
        error.status = response.status;
        throw error;
    }
    return response.json() as Promise<CollaborationHead>;
};

const isUnavailableError = (error: unknown): boolean =>
    error instanceof Error &&
    "status" in error &&
    [401, 403, 404, 410].includes(
        (error as Error & { status: number }).status,
    );

const collaborationActor = (
    head: Pick<CollaborationHead, "authorRole" | "ownerAccountType">,
): { changes: string; label: string } => {
    if (head.authorRole === "trainer")
        return { changes: t("zmiany trenera"), label: t("Trener") };
    if (head.ownerAccountType === "teacher")
        return {
            changes: t("zmiany właściciela projektu"),
            label: t("Właściciel projektu"),
        };
    return { changes: t("zmiany ucznia"), label: t("Uczeń") };
};

export const StudentSharePanel = ({
    applyPulledProject,
    cloudSaveGeneration,
    ensureSaved,
    noticeTarget,
    onAuthorizationFailure,
    projectId,
    projectName,
    remoteProjectId,
    session,
    vm,
}: {
    applyPulledProject: (payload: {
        name?: string;
        project: ArrayBuffer;
    }) => Promise<void>;
    cloudSaveGeneration: number;
    ensureSaved: () => Promise<boolean>;
    noticeTarget?: HTMLDivElement | null;
    onAuthorizationFailure?: () => void;
    projectId: string;
    projectName: string;
    remoteProjectId?: string;
    session?: AuthenticatedSession;
    vm: ScratchVm | undefined;
}) => {
    const [share, setShareState] = useState<StudentShare | undefined>(() =>
        readStoredShare(projectId),
    );
    const [loadingShare, setLoadingShare] = useState(Boolean(remoteProjectId));
    const authorizationFailureRef = useRef(onAuthorizationFailure);
    const [remoteHead, setRemoteHead] = useState<CollaborationHead>();
    const [status, setStatus] = useState(share ? t("Współpraca aktywna") : "");
    const [working, setWorking] = useState(false);
    const [retrySequence, setRetrySequence] = useState(0);
    const shareRef = useRef(share);
    const pushAction = useRef<() => Promise<void>>(() => Promise.resolve());
    const scheduledCloudSave = useRef(cloudSaveGeneration);
    const latestCloudSaveGeneration = useRef(cloudSaveGeneration);
    const skipNextAutoPush = useRef(false);
    const pushConflict = useRef(false);
    const knownRevision = useRef(share?.revision ?? 0);
    const pushTimer = useRef<number>();
    const successTimer = useRef<number>();
    const [synchronizationUnavailable, setSynchronizationUnavailable] =
        useState(false);
    const presenceId = useRef(window.crypto.randomUUID());
    const shareId = share?.id;
    const ownerCapability = share?.ownerCapability;
    const csrfToken = session?.csrfToken;

    useEffect(() => {
        latestCloudSaveGeneration.current = cloudSaveGeneration;
    }, [cloudSaveGeneration]);

    useEffect(() => {
        authorizationFailureRef.current = onAuthorizationFailure;
    }, [onAuthorizationFailure]);

    const setTransientSuccess = useCallback((message: string) => {
        window.clearTimeout(successTimer.current);
        setStatus(message);
        successTimer.current = window.setTimeout(
            () => setStatus(t("Współpraca aktywna")),
            4_000,
        );
    }, []);

    const updateShare = useCallback(
        (next: StudentShare | undefined) => {
            shareRef.current = next;
            if (next) knownRevision.current = Math.max(knownRevision.current, next.revision);
            else pushConflict.current = false;
            setShareState(next);
            if (next && (!remoteProjectId || next.ownerCapability))
                localStorage.setItem(
                    studentStorageKey(projectId),
                    JSON.stringify(next),
                );
            else if (!next) localStorage.removeItem(studentStorageKey(projectId));
        },
        [projectId, remoteProjectId],
    );

    useEffect(() => {
        if (!remoteProjectId) {
            const onStorage = (event: StorageEvent) => {
                if (event.key === studentStorageKey(projectId)) updateShare(readStoredShare(projectId));
            };
            window.addEventListener("storage", onStorage);
            return () => window.removeEventListener("storage", onStorage);
        }
        let cancelled = false;
        void fetch(`/api/collaboration/v1/projects/${remoteProjectId}/share`)
            .then(async (response) => {
                if (response.status === 404) return undefined;
                if (!response.ok) {
                    if ([401, 403].includes(response.status)) authorizationFailureRef.current?.();
                    throw new Error("COLLABORATION_SHARE_READ_FAILED");
                }
                return response.json() as Promise<{
                    session: {id: string; revision: number}; shareUrl: string;
                    key: string; trainerCapability: string;
                }>;
            })
            .then((body) => {
                if (cancelled) return;
                if (body) {
                    scheduledCloudSave.current = latestCloudSaveGeneration.current;
                    updateShare({id: body.session.id, revision: body.session.revision,
                        shareUrl: body.shareUrl, key: body.key,
                        trainerCapability: body.trainerCapability});
                    setStatus(t("Współpraca aktywna"));
                }
                setLoadingShare(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setStatus(t("Nie można sprawdzić linku do udostępniania."));
                    setLoadingShare(false);
                }
            });
        return () => { cancelled = true; };
    }, [projectId, remoteProjectId, updateShare]);

    const poll = useCallback(async () => {
        const current = shareRef.current;
        if (!current) return;
        try {
            const head = await readHead(
                current.id,
                current.ownerCapability,
                current.revision,
            );
            if (
                head &&
                shouldAcceptPolledRevision(knownRevision.current, head.revision)
            ) {
                knownRevision.current = head.revision;
                pushConflict.current = true;
                window.clearTimeout(pushTimer.current);
                window.clearTimeout(successTimer.current);
                setRemoteHead(head);
                if (head.authorRole)
                    setStatus(t("{value0} opublikował zmianę. Wczytaj ją przed dalszą synchronizacją.", {
                        value0: collaborationActor(head).label,
                    }));
            }
        } catch (error) {
            if (isUnavailableError(error)) {
                if (error instanceof Error && "status" in error &&
                    [401, 403].includes((error as Error & {status: number}).status)) authorizationFailureRef.current?.();
                setSynchronizationUnavailable(true);
                setStatus(t("Synchronizacja: niedostępna"));
                if (error instanceof Error && "status" in error && (error as Error & {status: number}).status === 404)
                    updateShare(undefined);
                trackTelemetryAction("collaboration_unavailable", { source: "owner_poll" });
            } else {
                setStatus(t("Nie można sprawdzić synchronizacji"));
                if (!(error instanceof Error && "status" in error))
                    trackTelemetryError(error, "handled_operation");
            }
        }
    }, [updateShare]);

    useEffect(() => {
        if (!share || synchronizationUnavailable) return;
        const interval = window.setInterval(() => void poll(), 3_000);
        void poll();
        return () => window.clearInterval(interval);
    }, [poll, share, synchronizationUnavailable]);

    useEffect(() => {
        if (!shareId || synchronizationUnavailable) return;
        const sendPresence = (method: "DELETE" | "PUT", keepalive = false) =>
            fetch(`/api/collaboration/v1/sessions/${shareId}/presence`, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
                    ...(ownerCapability
                        ? capabilityHeaders(ownerCapability)
                        : {}),
                },
                body: JSON.stringify({ clientId: presenceId.current }),
                keepalive,
            });
        const heartbeat = () => {
            void sendPresence("PUT").then((response) => {
                if ([401, 403].includes(response.status))
                    authorizationFailureRef.current?.();
            }).catch(() => undefined);
        };
        const deactivate = () => {
            void sendPresence("DELETE", true).catch(() => undefined);
        };
        heartbeat();
        const interval = window.setInterval(heartbeat, 15_000);
        window.addEventListener("pagehide", deactivate);
        window.addEventListener("pageshow", heartbeat);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener("pagehide", deactivate);
            window.removeEventListener("pageshow", heartbeat);
            deactivate();
        };
    }, [csrfToken, ownerCapability, shareId, synchronizationUnavailable]);

    const push = useCallback(async () => {
        const current = shareRef.current;
        if (!current || !vm) return;
        if (pushConflict.current) return;
        if (remoteHead && remoteHead.revision > current.revision) {
            pushConflict.current = true;
            trackTelemetryAction("collaboration_conflict", { source: "owner_push" });
            setStatus(
                t("{value0} opublikował zmianę. Wczytaj ją przed dalszą synchronizacją.", { value0: collaborationActor(remoteHead).label }),
            );
            return;
        }
        try {
            const nextRevision = current.revision + 1;
            const parentRevision =
                current.revision === 0 ? null : current.revision;
            const project = await (await vm.saveProjectSb3()).arrayBuffer();
            const envelope = await encryptProjectPayload(
                { name: projectName, project },
                current.key,
                current.id,
                nextRevision,
                parentRevision,
                "student",
            );
            const response = await fetch(
                `/api/collaboration/v1/sessions/${current.id}/revisions`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "If-Match": `"${current.revision}"`,
                        "Idempotency-Key": randomSecret(),
                        ...(session
                            ? { "X-CSRF-Token": session.csrfToken }
                            : {}),
                        ...(current.ownerCapability ? capabilityHeaders(current.ownerCapability) : {}),
                    },
                    body: JSON.stringify({ parentRevision, envelope }),
                },
            );
            if (response.status === 412) {
                if (shareRef.current?.revision !== current.revision) return;
                // eslint-disable-next-line require-atomic-updates -- The revision check discards responses from an earlier push.
                pushConflict.current = true;
                trackTelemetryAction("collaboration_conflict", { source: "owner_push" });
                await poll();
                return;
            }
            if ([401, 403, 404, 410].includes(response.status)) {
                if ([401, 403].includes(response.status)) authorizationFailureRef.current?.();
                setSynchronizationUnavailable(true);
                setStatus(t("Synchronizacja: niedostępna"));
                trackTelemetryAction("collaboration_unavailable", { source: "owner_push" });
                return;
            }
            if (!response.ok) throw new Error("COLLABORATION_PUSH_FAILED");
            const body = (await response.json()) as { revision: number };
            const hasNewerRevision = knownRevision.current > body.revision;
            updateShare({ ...current, revision: body.revision });
            if (!hasNewerRevision) {
                // eslint-disable-next-line require-atomic-updates -- This response matches the latest observed revision.
                pushConflict.current = false;
                setRemoteHead(undefined);
                setTransientSuccess(
                    session?.user.accountType === "teacher"
                        ? t("Zmiany właściciela projektu zostały wysłane.")
                        : t("Zmiany ucznia zostały wysłane."),
                );
            }
            trackTelemetryAction("collaboration_push", { result: "success", source: "owner" });
        } catch (error) {
            if (synchronizationUnavailable) return;
            setStatus(
                t("Nie udało się wysłać zmian. Lokalny projekt jest zapisany; synchronizacja spróbuje ponownie."),
            );
            setRetrySequence((value) => value + 1);
            trackTelemetryAction("collaboration_push", { result: "failure", source: "owner" });
            if (!(error instanceof Error && error.message === "COLLABORATION_PUSH_FAILED"))
                trackTelemetryError(error, "handled_operation");
        }
    }, [poll, projectName, remoteHead, session, setTransientSuccess, synchronizationUnavailable, updateShare, vm]);

    useEffect(() => {
        pushAction.current = push;
    }, [push]);

    useEffect(() => {
        if (retrySequence === 0 || !share) return;
        const timer = window.setTimeout(() => void push(), 10_000);
        return () => window.clearTimeout(timer);
    }, [push, retrySequence, share]);

    useEffect(() => {
        if (!share) return;
        const decision = nextOwnerPushState(
            scheduledCloudSave.current,
            cloudSaveGeneration,
            skipNextAutoPush.current,
            pushConflict.current,
        );
        scheduledCloudSave.current = decision.scheduledGeneration;
        skipNextAutoPush.current = decision.skipNext;
        if (!decision.shouldPush) return;
        window.clearTimeout(pushTimer.current);
        pushTimer.current = window.setTimeout(
            () => void pushAction.current(),
            1_500,
        );
        return () => window.clearTimeout(pushTimer.current);
    }, [cloudSaveGeneration, share]);

    useEffect(
        () => () => {
            window.clearTimeout(pushTimer.current);
            window.clearTimeout(successTimer.current);
        },
        [],
    );

    const createShare = async () => {
        if (!vm) return;
        setWorking(true);
        setStatus(t("Tworzenie bezpiecznego linku…"));
        try {
            if (!(await ensureSaved())) throw new Error("SAVE_REQUIRED");
            const ownerCapability = randomSecret();
            const trainerCapability = randomSecret();
            const key = randomSecret();
            const response = await fetch("/api/collaboration/v1/sessions", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(session
                        ? { "X-CSRF-Token": session.csrfToken }
                        : {}),
                },
                body: JSON.stringify({
                    key,
                    ownerCapability,
                    ...(remoteProjectId ? { projectId: remoteProjectId } : {}),
                    trainerCapability,
                }),
            });
            if (!response.ok) {
                if ([401, 403].includes(response.status)) authorizationFailureRef.current?.();
                throw new Error("COLLABORATION_SHARE_HTTP");
            }
            const body = (await response.json()) as {
                session: { id: string; revision: number };
                shareUrl: string; key?: string; trainerCapability?: string;
            };
            const next = {
                id: body.session.id,
                key: body.key ?? key,
                ...(remoteProjectId ? {} : {ownerCapability}),
                trainerCapability: body.trainerCapability ?? trainerCapability,
                revision: body.session.revision,
                shareUrl: body.shareUrl,
            };
            if (response.status === 200) scheduledCloudSave.current = cloudSaveGeneration;
            updateShare(next);
            registerTelemetrySensitiveValue(projectName);
            setSynchronizationUnavailable(false);
            setStatus(
                t("Link jest gotowy. Projekt może edytować zalogowany trener posiadający link."),
            );
            if (response.status === 201) {
                window.clearTimeout(pushTimer.current);
                pushTimer.current = window.setTimeout(
                    () => void pushAction.current(),
                    1_500,
                );
            }
            trackTelemetryAction("collaboration_share_create", { result: "success" });
        } catch (error) {
            setStatus(
                t("Nie udało się udostępnić projektu. Lokalna praca działa nadal."),
            );
            trackTelemetryAction("collaboration_share_create", { result: "failure" });
            if (!(error instanceof Error && ["COLLABORATION_SHARE_HTTP", "SAVE_REQUIRED"].includes(error.message)))
                trackTelemetryError(error, "handled_operation");
        } finally {
            setWorking(false);
        }
    };

    const pull = async () => {
        const current = shareRef.current;
        if (!current || !remoteHead?.envelope || !remoteHead.authorRole) return;
        const actor = collaborationActor(remoteHead);
        if (
            !window.confirm(
                t("Wczytanie {value0} nadpisze bieżący projekt i wszystkie niewysłane zmiany. Tej operacji nie można cofnąć. Kontynuować?", { value0: actor.changes }),
            )
        )
            return;
        setWorking(true);
        try {
            const parentRevision =
                remoteHead.revision === 1 ? null : remoteHead.revision - 1;
            const project = await decryptProjectPayload(
                remoteHead.envelope,
                current.key,
                current.id,
                remoteHead.revision,
                parentRevision,
                remoteHead.authorRole,
            );
            await applyPulledProject(project);
            skipNextAutoPush.current = true;
            pushConflict.current = false;
            updateShare({ ...current, revision: remoteHead.revision });
            setRemoteHead(undefined);
            setStatus(t("Wczytano {value0}.", { value0: actor.changes }));
            trackTelemetryAction("collaboration_pull", { result: "success", source: "owner" });
        } catch (error) {
            setStatus(
                t("Nie udało się wczytać zmian. Bieżący projekt pozostał bez zmian."),
            );
            trackTelemetryAction("collaboration_pull", { result: "failure", source: "owner" });
            trackTelemetryError(error, "handled_operation");
        } finally {
            setWorking(false);
        }
    };

    if (session?.user.accountType === "teacher") return null;
    if (loadingShare) return <p className="action-status" role="status">{t("Sprawdzanie linku…")}</p>;
    if (!share)
        return (
            <div className="share-controls">
                <button
                    className="share-project-button"
                    disabled={working || !vm}
                    onClick={() => void createShare()}
                    type="button"
                >
                    {t("Udostępnij projekt")}
                </button>
                {status && (
                    <p className="action-status" role="status">
                        {status}
                    </p>
                )}
            </div>
        );

    const link = trainerShareLink(
        window.location.origin,
        share.shareUrl,
        share.id,
        share.key,
        share.trainerCapability,
    );
    const remoteActor = remoteHead ? collaborationActor(remoteHead) : undefined;
    const updateNotice = remoteHead && (
        <section className={noticeTarget ? "project-card share-update" : "share-update"}>
            <p>{t("{actor} wprowadził zmiany. Możesz wczytać nową wersję.", { actor: remoteActor?.label ?? "" })}</p>
            <button
                className="trainer-update-load-button"
                disabled={working}
                onClick={() => void pull()}
                type="button"
            >
                {t("Wczytaj")} {remoteActor?.changes}
            </button>
        </section>
    );
    return (
        <>
            {updateNotice && (noticeTarget
                ? createPortal(updateNotice, noticeTarget)
                : updateNotice)}
            <div className="share-controls">
                <div>
                    <label className="project-name-field">
                        <span>{t("Link do udostępniania:")}</span>
                        <input aria-label={t("Link trenera")} readOnly value={link} />
                    </label>
                    <button
                        className="project-copy-link-button secondary-button"
                        disabled={working}
                        onClick={() => {
                            void navigator.clipboard.writeText(link).then(
                                () => {
                                    setStatus(t("Link skopiowany."));
                                    trackTelemetryAction("collaboration_share_copy", { result: "success" });
                                },
                                () => {
                                    setStatus(
                                        t("Nie udało się skopiować linku — zaznacz go ręcznie."),
                                    );
                                    trackTelemetryAction("collaboration_share_copy", { result: "failure" });
                                },
                            );
                        }}
                        type="button"
                    >
                        {t("Kopiuj link")}
                    </button>
                </div>
                <p className="action-status" role="status">{status}</p>
            </div>
        </>
    );
};

const loadTrainerSecrets = (
    sessionId: string,
): { capability: string; key: string } => {
    const key = trainerStorageKey(sessionId);
    if (window.location.hash) {
        const secrets = parseTrainerFragment(window.location.hash);
        sessionStorage.setItem(key, JSON.stringify(secrets));
        history.replaceState(null, "", window.location.pathname);
        return secrets;
    }
    const stored = sessionStorage.getItem(key);
    if (!stored) throw new Error(t("Brak capability w tej karcie"));
    return JSON.parse(stored) as { capability: string; key: string };
};

const TrainerWorkspace = ({
    loginError,
    onReauth,
    onSessionLost,
    onLogout,
    session,
    sessionValid,
    sessionId,
}: {
    loginError: string;
    onReauth: (session: AuthenticatedSession) => void;
    onSessionLost: () => void;
    onLogout: () => Promise<void>;
    session: AuthenticatedSession;
    sessionValid: boolean;
    sessionId: string;
}) => {
    const [secrets] = useState(() => {
        try {
            return { value: loadTrainerSecrets(sessionId), error: "" };
        } catch {
            return {
                value: undefined,
                error: "Link trenera jest nieprawidłowy lub wygasł",
            };
        }
    });
    const [head, setHead] = useState<CollaborationHead>();
    const [loadedRevision, setLoadedRevision] = useState(0);
    const [dirty, setDirty] = useState(false);
    const [projectName, setProjectName] = useState(createRandomProjectName);
    const [editorReady, setEditorReady] = useState(false);
    const [status, setStatus] = useState(
        t("Oczekiwanie na projekt właściciela…"),
    );
    const [working, setWorking] = useState(false);
    const [pushing, setPushing] = useState(false);
    const [importVisible, setImportVisible] = useState(false);
    const vmRef = useRef<ScratchVm>();
    const [assistantVm, setAssistantVm] = useState<ScratchVm>();
    const suppressChanges = useRef(true);
    const changeListenerRef = useRef<() => void>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const initialLoadAttempted = useRef(false);
    const knownRevision = useRef(0);
    const headRef = useRef<CollaborationHead>();
    const [synchronizationUnavailable, setSynchronizationUnavailable] =
        useState(false);

    useEffect(() => {
        headRef.current = head;
    }, [head]);

    const poll = useCallback(async () => {
        if (!secrets.value || !sessionValid) return;
        try {
            const next = await readHead(
                sessionId,
                secrets.value.capability,
                undefined,
                true,
            );
            if (
                next &&
                next.revision >= knownRevision.current
            ) {
                const revisionAdvanced = shouldAcceptPolledRevision(
                    knownRevision.current,
                    next.revision,
                );
                knownRevision.current = Math.max(
                    knownRevision.current,
                    next.revision,
                );
                headRef.current = next;
                setHead(next);
                if (
                    revisionAdvanced &&
                    next.revision > loadedRevision &&
                    next.authorRole
                )
                    setStatus(
                        collaborationChangeMessage(
                            "trainer",
                            next.authorRole,
                            next.ownerAccountType,
                        ),
                    );
            }
        } catch (error) {
            if (isUnavailableError(error)) {
                setSynchronizationUnavailable(true);
                setStatus(t("Synchronizacja: niedostępna"));
                trackTelemetryAction("collaboration_unavailable", { source: "trainer_poll" });
                onSessionLost();
            } else {
                setStatus(t("Nie można sprawdzić synchronizacji"));
                if (!(error instanceof Error && "status" in error))
                    trackTelemetryError(error, "handled_operation");
            }
        }
    }, [loadedRevision, onSessionLost, secrets.value, sessionId, sessionValid]);

    useEffect(() => {
        if (!secrets.value || synchronizationUnavailable || !sessionValid) return;
        const initial = window.setTimeout(() => void poll(), 0);
        const interval = window.setInterval(() => void poll(), 3_000);
        return () => {
            window.clearTimeout(initial);
            window.clearInterval(interval);
        };
    }, [poll, secrets.value, synchronizationUnavailable, sessionValid]);

    const onVmInit = useCallback((vm: ScratchVm) => {
        vmRef.current = vm;
        setAssistantVm(vm);
        setEditorReady(true);
        changeListenerRef.current = () => {
            if (!suppressChanges.current) {
                setDirty(true);
                setStatus(t("Lokalny draft ma niewysłane zmiany"));
            }
        };
        vm.on("PROJECT_CHANGED", changeListenerRef.current);
        suppressChanges.current = false;
    }, []);

    useEffect(
        () => () => {
            if (vmRef.current && changeListenerRef.current)
                vmRef.current.removeListener(
                    "PROJECT_CHANGED",
                    changeListenerRef.current,
                );
        },
        [],
    );

    const pull = useCallback(async ({
        confirmOverwrite = true,
    }: { confirmOverwrite?: boolean } = {}) => {
        if (
            !sessionValid ||
            !secrets.value ||
            !head?.envelope ||
            !head.authorRole ||
            !vmRef.current
        )
            return;
        const actor = collaborationActor(head);
        if (
            confirmOverwrite &&
            !window.confirm(
                t("Wczytanie {value0} nadpisze bieżący projekt i wszystkie niewysłane zmiany. Tej operacji nie można cofnąć. Kontynuować?", { value0: actor.changes }),
            )
        )
            return;
        setWorking(true);
        setStatus(t("Wczytywanie: {value0}…", { value0: actor.changes }));
        try {
            const parentRevision =
                head.revision === 1 ? null : head.revision - 1;
            const project = await decryptProjectPayload(
                head.envelope,
                secrets.value.key,
                sessionId,
                head.revision,
                parentRevision,
                head.authorRole,
            );
            registerTelemetrySensitiveValue(project.name);
            suppressChanges.current = true;
            await vmRef.current.loadProject(project.project);
            await settleProject();
            suppressChanges.current = false;
            if (project.name) {
                setProjectName(project.name);
                setTrainerTabTitle(project.name);
            }
            setLoadedRevision(head.revision);
            setDirty(false);
            setStatus(t("Wczytano {value0}.", { value0: actor.changes }));
            trackTelemetryAction("collaboration_pull", { result: "success", source: "trainer" });
        } catch (error) {
            suppressChanges.current = false;
            setStatus(
                t("Nie udało się wczytać zmian. Projekt pozostał bez zmian."),
            );
            trackTelemetryAction("collaboration_pull", { result: "failure", source: "trainer" });
            trackTelemetryError(error, "handled_operation");
        } finally {
            setWorking(false);
        }
    }, [head, secrets.value, sessionId, sessionValid]);

    useEffect(() => {
        if (
            initialLoadAttempted.current ||
            !editorReady ||
            working ||
            loadedRevision !== 0 ||
            !head?.envelope
        )
            return;
        initialLoadAttempted.current = true;
        void pull({ confirmOverwrite: false });
    }, [editorReady, head, loadedRevision, pull, working]);

    const push = async () => {
        if (!sessionValid || !secrets.value || !head || !vmRef.current || loadedRevision === 0)
            return;
        if (head.revision !== loadedRevision) {
            trackTelemetryAction("collaboration_conflict", { source: "trainer_push" });
            if (head.authorRole)
                setStatus(
                    collaborationChangeMessage(
                        "trainer",
                        head.authorRole,
                        head.ownerAccountType,
                    ),
                );
            return;
        }
        setWorking(true);
        setPushing(true);
        try {
            const normalizedName = projectName.trim() || createRandomProjectName();
            if (normalizedName !== projectName) setProjectName(normalizedName);
            const nextRevision = head.revision + 1;
            const project = await (
                await vmRef.current.saveProjectSb3()
            ).arrayBuffer();
            const envelope = await encryptProjectPayload(
                { name: normalizedName, project },
                secrets.value.key,
                sessionId,
                nextRevision,
                head.revision,
                "trainer",
            );
            const response = await fetch(
                `/api/collaboration/v1/sessions/${sessionId}/revisions`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "If-Match": `"${head.revision}"`,
                        "Idempotency-Key": randomSecret(),
                        ...csrfHeaders(session),
                        ...capabilityHeaders(secrets.value.capability),
                    },
                    body: JSON.stringify({
                        parentRevision: head.revision,
                        envelope,
                    }),
                },
            );
            if (response.status === 412) {
                trackTelemetryAction("collaboration_conflict", { source: "trainer_push" });
                const latest = await readHead(
                    sessionId,
                    secrets.value.capability,
                );
                if (latest) {
                    setHead(latest);
                    headRef.current = latest;
                    knownRevision.current = Math.max(
                        knownRevision.current,
                        latest.revision,
                    );
                    if (latest.authorRole)
                        setStatus(
                            collaborationChangeMessage(
                                "trainer",
                                latest.authorRole,
                                latest.ownerAccountType,
                            ),
                        );
                }
                return;
            }
            if ([401, 403, 404, 410].includes(response.status)) {
                setSynchronizationUnavailable(true);
                setStatus(t("Synchronizacja: niedostępna"));
                trackTelemetryAction("collaboration_unavailable", { source: "trainer_push" });
                onSessionLost();
                return;
            }
            if (!response.ok) throw new Error("COLLABORATION_PUSH_HTTP");
            const body = (await response.json()) as { revision: number };
            const nextHead: CollaborationHead = {
                revision: body.revision,
                authorRole: "trainer",
                envelope,
                expiresAt: head.expiresAt,
                ownerAccountType: head.ownerAccountType,
            };
            setHead(nextHead);
            headRef.current = nextHead;
            knownRevision.current = body.revision;
            setLoadedRevision(body.revision);
            setDirty(false);
            setStatus(t("Zmiany wysłane."));
            trackTelemetryAction("collaboration_push", { result: "success", source: "trainer" });
        } catch (error) {
            setStatus(
                t("Nie udało się wysłać zmian. Projekt pozostał w tej karcie."),
            );
            trackTelemetryAction("collaboration_push", { result: "failure", source: "trainer" });
            if (!(error instanceof Error && error.message === "COLLABORATION_PUSH_HTTP"))
                trackTelemetryError(error, "handled_operation");
        } finally {
            setPushing(false);
            setWorking(false);
        }
    };

    const forcePush = async () => {
        if (
            !sessionValid ||
            !secrets.value ||
            !head ||
            !vmRef.current ||
            !dirty ||
            loadedRevision === 0
        )
            return;
        const actor = collaborationActor(head);
        if (
            !window.confirm(
                t("Force push nadpisze nowsze {value0} wersją trenera. Tej operacji nie można cofnąć. Kontynuować?", { value0: actor.changes }),
            )
        )
            return;
        setWorking(true);
        setPushing(true);
        try {
            const latest = await readHead(
                sessionId,
                secrets.value.capability,
            );
            if (!latest?.envelope || !latest.authorRole) throw new Error();
            const latestParent =
                latest.revision === 1 ? null : latest.revision - 1;
            await decryptProjectPayload(
                latest.envelope,
                secrets.value.key,
                sessionId,
                latest.revision,
                latestParent,
                latest.authorRole,
            );
            const normalizedName =
                projectName.trim() || createRandomProjectName();
            if (normalizedName !== projectName) setProjectName(normalizedName);
            const nextRevision = latest.revision + 1;
            const project = await (
                await vmRef.current.saveProjectSb3()
            ).arrayBuffer();
            const envelope = await encryptProjectPayload(
                { name: normalizedName, project },
                secrets.value.key,
                sessionId,
                nextRevision,
                latest.revision,
                "trainer",
            );
            const response = await fetch(
                `/api/collaboration/v1/sessions/${sessionId}/revisions`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "If-Match": `"${latest.revision}"`,
                        "Idempotency-Key": randomSecret(),
                        ...csrfHeaders(session),
                        ...capabilityHeaders(secrets.value.capability),
                    },
                    body: JSON.stringify({
                        parentRevision: latest.revision,
                        envelope,
                    }),
                },
            );
            if (response.status === 412) {
                trackTelemetryAction("collaboration_conflict", { source: "trainer_force_push" });
                const newer = await readHead(
                    sessionId,
                    secrets.value.capability,
                );
                if (newer) {
                    setHead(newer);
                    headRef.current = newer;
                    knownRevision.current = Math.max(
                        knownRevision.current,
                        newer.revision,
                    );
                    const newerActor = collaborationActor(newer);
                    setStatus(
                        t("{value0} opublikował kolejną zmianę. Ponów Force push i potwierdź ponownie.", { value0: newerActor.label }),
                    );
                }
                return;
            }
            if ([401, 403, 404, 410].includes(response.status)) {
                setSynchronizationUnavailable(true);
                setStatus(t("Synchronizacja: niedostępna"));
                trackTelemetryAction("collaboration_unavailable", { source: "trainer_force_push" });
                onSessionLost();
                return;
            }
            if (!response.ok) throw new Error("COLLABORATION_FORCE_PUSH_HTTP");
            const body = (await response.json()) as { revision: number };
            const nextHead: CollaborationHead = {
                revision: body.revision,
                authorRole: "trainer",
                envelope,
                expiresAt: latest.expiresAt,
                ownerAccountType: latest.ownerAccountType,
            };
            setHead(nextHead);
            headRef.current = nextHead;
            knownRevision.current = body.revision;
            setLoadedRevision(body.revision);
            setDirty(false);
            setStatus(t("Zmiany wysłane przez Force push."));
            trackTelemetryAction("collaboration_force_push", { result: "success" });
        } catch (error) {
            setStatus(
                t("Nie udało się wykonać Force push. Projekt pozostał w tej karcie."),
            );
            trackTelemetryAction("collaboration_force_push", { result: "failure" });
            if (!(error instanceof Error && ("status" in error || error.message === "COLLABORATION_FORCE_PUSH_HTTP")))
                trackTelemetryError(error, "handled_operation");
        } finally {
            setPushing(false);
            setWorking(false);
        }
    };

    const updateAvailable = Boolean(
        head?.envelope && head.revision > loadedRevision,
    );
    const incomingActor = head ? collaborationActor(head) : undefined;

    const loadFile = async (file: File) => {
        if (
            !sessionValid ||
            updateAvailable ||
            !vmRef.current ||
            !file.name.toLowerCase().endsWith(".sb3") ||
            file.size === 0 ||
            file.size > 20 * 1024 * 1024
        ) {
            setStatus(t("Wybierz prawidłowy, niepusty plik .sb3."));
            trackTelemetryAction("project_import", { reason: "invalid_file", result: "rejected", source: "file" });
            return;
        }
        if (
            !window.confirm(
                t("Wczytanie projektu zastąpi bieżącą zawartość i wszystkie niewysłane zmiany. Tej operacji nie można cofnąć. Kontynuować?"),
            )
        )
            return;
        setWorking(true);
        try {
            registerTelemetrySensitiveValue(file.name);
            suppressChanges.current = true;
            await vmRef.current.loadProject(await file.arrayBuffer());
            await settleProject();
            suppressChanges.current = false;
            setDirty(true);
            setStatus(
                t("Wczytano projekt. Możesz wysłać go {value0} przez Push.", { value0: head?.ownerAccountType === "teacher" ? t("właścicielowi projektu") : t("uczniowi") }),
            );
            trackTelemetryAction("project_import", { result: "success", source: "file" });
        } catch (error) {
            suppressChanges.current = false;
            setStatus(
                t("Nie udało się odczytać pliku .sb3. Projekt pozostał bez zmian."),
            );
            trackTelemetryAction("project_import", { result: "failure", source: "file" });
            trackTelemetryError(error, "handled_operation");
        } finally {
            setWorking(false);
        }
    };

    const loadLink = async (url: string) => {
        if (!sessionValid || updateAvailable || !vmRef.current || loadedRevision === 0)
            throw new Error(t("Edytor Scratch nie jest gotowy do wczytania projektu"));
        if (
            !window.confirm(
                t("Wczytanie projektu zastąpi bieżącą zawartość i wszystkie niewysłane zmiany. Tej operacji nie można cofnąć. Kontynuować?"),
            )
        )
            return false;
        setWorking(true);
        try {
            const imported = await loadScratchProject(url);
            registerTelemetrySensitiveValue(imported.title);
            suppressChanges.current = true;
            await vmRef.current.loadProject(JSON.stringify(imported.project));
            await settleProject();
            suppressChanges.current = false;
            setProjectName(imported.title);
            setDirty(true);
            setStatus(
                t("Wczytano projekt z linku. Możesz wysłać go {value0} przez Push.", { value0: head?.ownerAccountType === "teacher" ? t("właścicielowi projektu") : t("uczniowi") }),
            );
            return true;
        } catch (error) {
            suppressChanges.current = false;
            setStatus(t("Nie udało się wczytać projektu z linku. Projekt pozostał bez zmian."));
            throw error;
        } finally {
            setWorking(false);
        }
    };

    if (secrets.error)
        return <TrainerAccessFrame mode="invalid" onLogin={() => undefined} />;
    const synchronizationStatus = trainerSynchronizationStatus({
        authorRole: head?.authorRole,
        ownerAccountType: head?.ownerAccountType,
        ownerActive: head?.ownerActive,
        unavailable: synchronizationUnavailable,
        updateAvailable,
    });
    const panel = (
        <>
            <PanelHeader
                subtitle={authenticatedAccountLabel(session)}
                title={TRAINER_PANEL_TITLE}
            />
            <StatusGrid
                items={[
                    {
                        label: t("Edytor"),
                        value: editorReady ? t("gotowy") : t("ładowanie…"),
                        state: editorReady ? "ready" : "loading",
                    },
                    {
                        label: t("Synchronizacja"),
                        value: synchronizationStatus.value,
                        state: synchronizationStatus.state,
                    },
                ]}
            />
            {!sessionValid && (
                <section className="application-card" role="alert">
                    <p>{t("Sesja trenera wygasła. Niewysłany projekt pozostał w tej karcie; możesz pobrać plik .sb3.")}</p>
                    {loginError && <p className="form-error" role="alert">{loginError}</p>}
                    <StudentLoginForm codeLabel="Kod trenera" disabled={false}
                        formLabel="Ponowne logowanie trenera" onLogin={onReauth} />
                </section>
            )}
            {updateAvailable && (
                <section className="project-card share-update">
                    <p>{t("{actor} wprowadził zmiany. Możesz wczytać nową wersję.", { actor: incomingActor?.label ?? "" })}</p>
                    <button
                        disabled={!sessionValid || working || !editorReady}
                        onClick={() => void pull()}
                        type="button"
                    >
                        {t("Wczytaj")} {incomingActor?.changes}
                    </button>
                </section>
            )}
            <section className="project-card project-file-card">
                <h2>{t("Projekt")}</h2>
                <label className="project-name-field">
                    <span>{t("Nazwa projektu:")}</span>
                    <input
                        aria-label={t("Nazwa projektu")}
                        disabled={
                            !sessionValid || working || updateAvailable || loadedRevision === 0
                        }
                        maxLength={100}
                        onChange={(event) => {
                            setProjectName(event.target.value);
                            setDirty(true);
                            setStatus(t("Lokalny draft ma niewysłane zmiany"));
                        }}
                        value={projectName}
                    />
                </label>
                <div className="project-file-actions">
                    <button
                        disabled={working || loadedRevision === 0}
                        onClick={() =>
                            vmRef.current &&
                            void downloadProject(vmRef.current, projectName)
                        }
                        type="button"
                    >
                        {t("pobierz")}
                    </button>
                    <button
                        disabled={
                            !sessionValid || working || updateAvailable || loadedRevision === 0
                        }
                        onClick={() => setImportVisible(true)}
                        type="button"
                    >
                        {t("wczytaj")}
                    </button>
                </div>
                <input
                    accept=".sb3"
                    hidden
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) void loadFile(file);
                    }}
                    ref={fileInputRef}
                    type="file"
                />
            </section>
            <section className="project-card push-card">
                <div className="push-actions">
                    <button
                        disabled={
                            !sessionValid || working ||
                            !dirty ||
                            updateAvailable ||
                            loadedRevision === 0
                        }
                        onClick={() => void push()}
                        type="button"
                    >
                        {t("Push")}
                    </button>
                    <button
                        className="force-push-button"
                        disabled={
                            !sessionValid || working ||
                            !dirty ||
                            !updateAvailable ||
                            loadedRevision === 0
                        }
                        onClick={() => void forcePush()}
                        type="button"
                    >
                        {t("Force push")}
                    </button>
                </div>
                <p className="action-status" role="status">
                    {pushing ? t("Wysyłanie zmian trenera…") : status}
                </p>
            </section>
            <AiAssistant session={sessionValid ? session : undefined} vm={editorReady ? assistantVm : undefined} projectId={sessionId} />
            <section className="application-card account-actions">
                <button
                    className="secondary-button"
                    disabled={!sessionValid}
                    onClick={() => void onLogout()}
                    type="button"
                >
                    {t("Wyloguj")}
                </button>
            </section>
        </>
    );
    return (
        <>
            <EditorShell panel={panel}>
                <ScratchEditor onVmInit={onVmInit} />
            </EditorShell>
            {importVisible && (
                <ProjectImportDialog
                    onCancel={() => setImportVisible(false)}
                    onChooseFile={() => {
                        setImportVisible(false);
                        fileInputRef.current?.click();
                    }}
                    onLoadLink={async (url) => {
                        const loaded = await loadLink(url);
                        if (loaded) setImportVisible(false);
                        return loaded;
                    }}
                />
            )}
        </>
    );
};

export const TrainerApp = ({ sessionId }: { sessionId: string }) => {
    const [secrets] = useState(() => {
        try {
            return { error: "", value: loadTrainerSecrets(sessionId) };
        } catch {
            return { error: "Link trenera jest nieprawidłowy lub wygasł", value: undefined };
        }
    });
    const [session, setSession] = useState<Session>();
    const [loginError, setLoginError] = useState("");
    const [linkAccess, setLinkAccess] = useState<"checking" | "invalid" | "ready">("checking");
    const [verifiedTeacherId, setVerifiedTeacherId] = useState("");
    const [workspaceSession, setWorkspaceSession] = useState<AuthenticatedSession>();
    const sessionCheck = useRef(0);

    const refreshSession = useCallback((invalidate = false) => {
        const check = ++sessionCheck.current;
        if (invalidate) setLinkAccess("checking");
        void readSession()
            .then((next) => {
                if (check === sessionCheck.current) setSession(next);
            })
            .catch(() => {
                if (check === sessionCheck.current) setSession({ authenticated: false });
            });
    }, []);

    useEffect(() => {
        const check = ++sessionCheck.current;
        void readSession()
            .then((next) => {
                if (check === sessionCheck.current) setSession(next);
            })
            .catch(() => {
                if (check === sessionCheck.current) setSession({ authenticated: false });
            });
    }, []);

    useEffect(() => {
        const unsubscribe = subscribeToLogout(() => refreshSession(true));
        const onFocus = () => refreshSession();
        window.addEventListener("focus", onFocus);
        return () => {
            unsubscribe();
            window.removeEventListener("focus", onFocus);
        };
    }, [refreshSession]);

    useEffect(() => {
        if (!session || !hasTrainerAccess(session) || !secrets.value) return;
        if (workspaceSession && workspaceSession.user.id !== session.user.id) {
            return;
        }
        let cancelled = false;
        const check = sessionCheck.current;
        void readHead(sessionId, secrets.value.capability)
            .then(() => {
                if (!cancelled && check === sessionCheck.current) {
                    setVerifiedTeacherId(session.user.id);
                    setLinkAccess("ready");
                    setWorkspaceSession(session);
                    setLoginError("");
                }
            })
            .catch((error: unknown) => {
                if (cancelled || check !== sessionCheck.current) return;
                const status = error instanceof Error && "status" in error
                    ? (error as Error & { status: number }).status
                    : 0;
                if (trainerPreflightFailure(status) === "login-required") {
                    setLinkAccess("checking");
                    setVerifiedTeacherId("");
                    setSession({ authenticated: false });
                } else {
                    setVerifiedTeacherId(session.user.id);
                    setLinkAccess("invalid");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [secrets.value, session, sessionId, workspaceSession]);

    if (secrets.error)
        return <TrainerAccessFrame mode="invalid" onLogin={() => undefined} />;
    if (!session)
        return <TrainerAccessFrame mode="checking" onLogin={() => undefined} />;
    if (!hasTrainerAccess(session) && !workspaceSession)
        return (
            <TrainerAccessFrame
                loginError={loginError}
                mode="login"
                onLogin={(authenticatedSession) => {
                    if (authenticatedSession.user.accountType !== "teacher") {
                        trackTelemetryAction("login", {
                            accountType: authenticatedSession.user.accountType,
                            reason: "teacher_required",
                            result: "rejected",
                        });
                        setLoginError(t("Podany kod nie należy do konta trenera."));
                        setSession(authenticatedSession);
                        return;
                    }
                    setLoginError("");
                    setLinkAccess("checking");
                    setVerifiedTeacherId("");
                    setSession(authenticatedSession);
                }}
            />
        );
    if (hasTrainerAccess(session) && verifiedTeacherId === session.user.id && linkAccess === "invalid" && !workspaceSession)
        return <TrainerAccessFrame mode="invalid" onLogin={() => undefined} />;
    if (!workspaceSession && (!hasTrainerAccess(session) || linkAccess !== "ready" || verifiedTeacherId !== session.user.id))
        return <TrainerAccessFrame mode="checking" onLogin={() => undefined} />;

    const sessionValid = hasTrainerAccess(session) && linkAccess === "ready" &&
        verifiedTeacherId === session.user.id && session.user.id === workspaceSession?.user.id;
    const currentSession = hasTrainerAccess(session) && sessionValid ? session : workspaceSession;
    if (!currentSession) return <TrainerAccessFrame mode="checking" onLogin={() => undefined} />;

    return (
        <TrainerWorkspace
            loginError={loginError}
            onReauth={(authenticatedSession) => {
                if (authenticatedSession.user.accountType !== "teacher" ||
                    authenticatedSession.user.id !== workspaceSession?.user.id) {
                    setLoginError(t("Zaloguj się ponownie na to samo konto trenera, aby wznowić pracę."));
                } else setLoginError("");
                setLinkAccess("checking");
                setVerifiedTeacherId("");
                setSession(authenticatedSession);
            }}
            onLogout={async () => {
                try {
                    const response = await fetch("/api/auth/session", {
                        method: "DELETE",
                        headers: csrfHeaders(currentSession),
                    });
                    if (response.ok) {
                        trackTelemetryAction("logout", { accountType: "teacher", result: "success" });
                        setLinkAccess("checking");
                        setVerifiedTeacherId("");
                        setSession({ authenticated: false });
                        broadcastLogout();
                    } else trackTelemetryAction("logout", { accountType: "teacher", result: "failure" });
                } catch (caught) {
                    trackTelemetryAction("logout", { accountType: "teacher", result: "failure" });
                    trackTelemetryError(caught, "handled_operation");
                }
            }}
            onSessionLost={() => refreshSession(true)}
            session={currentSession}
            sessionValid={sessionValid}
            sessionId={sessionId}
        />
    );
};
